import { useCallback, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, Flex, Input, Select } from 'antd';
import { getSelectsOptions } from '@ohpen/enso-ui';
import { useTranslation } from 'react-i18next';

import { FieldType } from '@pmp/api';
import { NotesListProps } from '../interfaces';
import { Note } from './Note';
import styles from '../styles.module.css';

export const NotesList = ({ notes, valueLists, onEdit, onCreate, onDelete, userName }: NotesListProps) => {
  const { t } = useTranslation();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [searchValue, setSearchValue] = useState('');

  const filteredNotes = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const matchesSearch = (note: (typeof notes)[number]) => {
      if (!query) return true;
      const text = (note.text ?? '').toLowerCase();
      const user = (note.user ?? '').toLowerCase();
      const category = (note.category ?? '').toLowerCase();
      return text.includes(query) || user.includes(query) || category.includes(query);
    };

    return notes.filter(note => {
      const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(note.category);
      const matchesUser = selectedUsers.length === 0 || selectedUsers.includes(note.user);
      const matchesDate = !selectedDate || dayjs(note.date).isSame(selectedDate, 'day');
      return matchesCategory && matchesUser && matchesDate && matchesSearch(note);
    });
  }, [notes, selectedCategories, selectedUsers, selectedDate, searchValue]);

  const userOptions = useMemo(() => {
    const users = Array.from(new Set(notes.map(note => note.user)));
    return users.map(user => ({ label: user, value: user }));
  }, [notes]);

  const categoryOptions = useMemo(() => {
    const selectOptions = (valueLists && getSelectsOptions(valueLists)) || {};
    return selectOptions[FieldType.NOTE_CATEGORY];
  }, [valueLists]);

  const list = useMemo(
    () =>
      filteredNotes.map(note => (
        <Note key={note.id} note={note} onEdit={onEdit} onDelete={onDelete} userName={userName} categoryOptions={categoryOptions} />
      )),
    [filteredNotes, onEdit, onDelete, userName, categoryOptions],
  );

  const onChangeSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  const onChangeCategories = useCallback((values: string[]) => {
    setSelectedCategories(values ?? []);
  }, []);

  const onChangeUsers = useCallback((values: string[]) => {
    setSelectedUsers(values ?? []);
  }, []);

  const onChangeDate = useCallback((value: Dayjs | null) => {
    setSelectedDate(value);
  }, []);

  return (
    <Flex vertical>
      <Flex gap={8} className={styles.notesFilters}>
        <Input id="search" placeholder={t('global.placeholders.search')} allowClear value={searchValue} onChange={onChangeSearch} className={styles.searchInput} />
        <DatePicker value={selectedDate ?? undefined} onChange={onChangeDate} className={styles.filterControl} />
        <Select
          mode="multiple"
          allowClear
          placeholder={t('global.placeholders.selectCategories')}
          onChange={onChangeCategories}
          options={categoryOptions}
          className={styles.filterControl}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder={t('global.placeholders.selectUsers')}
          onChange={onChangeUsers}
          options={userOptions}
          className={styles.filterControl}
        />
      </Flex>
      <Flex className={styles.notesWrapper}>{list}</Flex>
    </Flex>
  );
};
